import React, { useState, useEffect, useMemo } from 'react'
import { createUseStyles } from 'react-jss'
import { formatPrice } from '../helpers/format'
import ResponsiveImage from './ResponsiveImage'
import BuyButton from './Slices/ShopProducts/BuyButton'
import Link from './Link'
import YotpoStarRatings from './YotpoStarRatings'
import theme from '../style/theme'
import detectIt from 'detect-it'
import { vw } from '../style/vw'
import star from '../images/star.svg'

const RelatedProductCard = ({ product, size = 'large', imageMaxHeightClass }) => {
  const classes = useStyles()
  const [quantity, setQuantity] = useState(1)
  const [selectedVariantId, setSelectedVariantId] = useState(null)
  const productTitle = product.store?.title || product.title
  // Use the actual store slug, not a manually generated one
  const productSlug = product.store?.slug
  // console.log('🔗 RelatedProductCard: Product slug data:', {
  //   productTitle,
  //   storeSlug: product.store?.slug,
  //   usingSlug: productSlug
  // })
  // Set default variant when product loads
  useEffect(() => {
    if (product.store?.variants && product.store.variants.length > 0 && product.store.variants[0]) {
      const firstVariant = product.store.variants[0]
      // Use the full variant ID as it comes from the store
      const variantId = firstVariant.id
      // console.log('🔗 RelatedProductCard: Setting variant ID:', variantId)
      setSelectedVariantId(variantId)
    }
  }, [product])

  // Create responsive image URLs with proper srcset
  const mainImageUrl = product.store?.previewImageUrl || '/placeholder-product.jpg'
  const createResponsiveUrl = (url, width) => {
    if (url.startsWith('/')) {
      // For local images (like placeholder)
      return url
    }
    // For Shopify/external images
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`
  }

  const image = useMemo(() => {
    return {
      src: mainImageUrl,
      alt: productTitle,
      sizes: size === 'large' ? [
        { url: createResponsiveUrl(mainImageUrl, 435), width: 435 },
        { url: createResponsiveUrl(mainImageUrl, 600), width: 600 },
        { url: createResponsiveUrl(mainImageUrl, 800), width: 800 }
      ] : [
        { url: createResponsiveUrl(mainImageUrl, 321), width: 321 },
        { url: createResponsiveUrl(mainImageUrl, 600), width: 600 },
        { url: createResponsiveUrl(mainImageUrl, 800), width: 800 }
      ]
    }
  }, [mainImageUrl, productTitle, size])

  const priceRange = product.store?.priceRange || {}
  const price = priceRange.minVariantPrice
  const comparePrice = priceRange.maxVariantPrice
  const status = product.store?.status
  const available = status === 'active'
  const comingSoon = status === 'draft'

  // console.log('🔗 RelatedProductCard: Product availability data:', {
  //   productTitle,
  //   status,
  //   available,
  //   comingSoon,
  //   selectedVariantId
  // })

  const isBestseller = product?.store?.isBestseller
  const aspect = size === 'large' ? 435 / 590 : 321 / 350
  return (
    <div className={classes.card}>
      <Link to={`/our-products/${productSlug}`} className={classes.productLink}>
        <div className={imageMaxHeightClass ? `${classes.imageWrapper} ${imageMaxHeightClass}` : classes.imageWrapper}>
          {isBestseller && (
            <div className={classes.bestsellerBadge}>
              <img src={star} alt='Bestseller' />
              <span>Bestseller</span>
            </div>
          )}
          <ResponsiveImage {...image} className={classes.image} aspect={aspect} />
        </div>
        <div className={classes.details}>
          <h3 className={classes.title}>{productTitle}</h3>
          {available && (
            <div className={classes.priceRatingContainer}>
              <div className={classes.priceContainer}>
                <span className={classes.price}>{formatPrice(price)}</span>
                {comparePrice && comparePrice !== price && (
                  <span className={classes.comparePrice}>
                    {formatPrice(comparePrice)}
                  </span>
                )}
              </div>
              <YotpoStarRatings
                product={product}
                size='small'
                sectionId='related-product-card'
                showText
              />
            </div>
          )}
        </div>
      </Link>
      {!available && (
        <div className={classes.availability}>
          {comingSoon ? 'Coming Soon' : 'Out of Stock'}
        </div>
      )}
      {available && selectedVariantId && (
        <div className={classes.buyButtonContainer}>
          {(() => {
            const productForBuyButton = {
              ...product,
              slug: product.store?.slug || product.slug,
              availableForSale: product.store?.status === 'active',
              store: product.store
            }
            // console.log('🔗 RelatedProductCard: Passing to BuyButton:', {
            //   productTitle,
            //   availableForSale: productForBuyButton.availableForSale,
            //   status: product.store?.status,
            //   variantId: selectedVariantId,
            //   slug: productForBuyButton.slug
            // })
            return (
              <BuyButton
                product={productForBuyButton}
                variantId={selectedVariantId}
                quantity={quantity}
                setQuantity={setQuantity}
                sellingPlanId={null}
              />
            )
          })()}
        </div>
      )}
    </div>
  )
}

const useStyles = createUseStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    border: `0px solid ${theme.colors.border}`,
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    overflow: 'visible',
    ...(detectIt.primaryInput === 'touch' ? {} : {
      '& $image img': {
        transition: 'transform 1.6s ease-in-out'
      },
      '&:hover $image img': {
        transform: 'scale(1.1)'
      }
    })
  },
  productLink: {
    display: 'block',
    textDecoration: 'none',
    color: 'inherit',
    flex: 1
  },
  imageWrapper: {
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
    background: '#f5f5f5'
  },
  bestsellerBadge: {
    position: 'absolute',
    top: '12px',
    left: '12px',
    background: 'rgba(255,255,255,0.8)',
    zIndex: 10,
    padding: '4px 8px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',

    '& img': {
      width: '13px',
      height: '13px'
    },

    '& span': {
      fontSize: vw(12),
      lineHeight: '1.2em',
      color: theme.colors.primary,
      [theme.breakpoints.up('md')]: {
        fontSize: vw(12, 'desktop')
      }
    }
  },
  image: {
    objectFit: 'cover',
    width: '100%',
    height: '100%',
    '& span': {
      transform: 'translateZ(0)'
    }
  },
  details: {
    padding: '10px 0px 10px 0px',
    flex: 1
  },
  title: {
    margin: '0',
    fontSize: vw(24),
    lineHeight: '1.2em',
    fontFamily: theme.fonts.headings,
    fontWeight: theme.fonts.headingsFontWeight,
    color: theme.colors.primary,
    [theme.breakpoints.up('md')]: {
      fontSize: vw(24, 'desktop')
    }
  },
  priceContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem'
  },
  price: {
    fontSize: vw(16),
    fontFamily: 'Morion',
    lineHeight: '2em',
    fontWeight: '700',
    color: theme.colors.primary,
    [theme.breakpoints.up('md')]: {
      fontSize: vw(16, 'desktop')
    }
  },
  comparePrice: {
    fontSize: vw(13),
    lineHeight: '2em',
    textDecoration: 'line-through',
    color: theme.colors.primary,
    opacity: 0.5,
    [theme.breakpoints.up('md')]: {
      fontSize: vw(13, 'desktop')
    }
  },
  availability: {
    display: 'block',
    fontWeight: '400',
    color: theme.colors.error,
    textAlign: 'center',
    fontSize: vw(14),
    lineHeight: 'calc(22/18)',
    padding: [9.5, 32],
    border: `1px solid ${theme.colors.error}`,
    [theme.breakpoints.up('md')]: {
      fontSize: vw(14, 'desktop')
    }
  },
  buyButtonContainer: {
    padding: '0rem',
    backgroundColor: 'transparent',
    borderTop: '1px solid #38524F'
  },
  priceRatingContainer: {
    display: 'flex',
    // Widget alignment styling
    '& .yotpo-reviews-star-ratings-widget': {
      display: 'flex !important',
      justifyContent: 'right !important',
      marginBottom: '0px !important'
    }
  }
}, { name: 'RelatedProductCard' })

export default RelatedProductCard
